/**
 * Prepare a photo for a tree circle: decode, rotate per EXIF (the browser does
 * this for us with `createImageBitmap` where supported), crop to a centred
 * square and downscale. Keeps uploads small and every circle the same shape.
 */
export interface PreparedImage {
  blob: Blob;
  width: number;
  height: number;
}

const MAX_SIDE = 640;

export async function preparePortrait(file: Blob): Promise<PreparedImage> {
  const bitmap = await decode(file);
  const side = Math.min(bitmap.width, bitmap.height);
  const out = Math.min(MAX_SIDE, side);
  const sx = (bitmap.width - side) / 2;
  const sy = (bitmap.height - side) / 2;
  const canvas = document.createElement('canvas');
  canvas.width = out;
  canvas.height = out;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas unsupported');
  ctx.drawImage(bitmap, sx, sy, side, side, 0, 0, out, out);
  if ('close' in bitmap) (bitmap as ImageBitmap).close();
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.86));
  if (!blob) throw new Error('encode failed');
  return { blob, width: out, height: out };
}

/**
 * A thumbnail for a circle on a constellation.
 *
 * A shared picture is stored at up to 1600px, which is right for looking at
 * and wrong for a 52px circle: one person's ring can hold hundreds of them,
 * and a full-size decode apiece is what makes a screen like that crawl. So the
 * first time a picture is wanted small it is decoded once, drawn into a square
 * a couple of hundred pixels a side, and kept — every later visit reads a few
 * kilobytes instead of a few hundred. Cropped to a square because the circle
 * crops it anyway, so the pixels a square throws away are pixels nobody sees.
 */
export async function prepareThumb(file: Blob, side = 192): Promise<PreparedImage> {
  const bitmap = await decode(file);
  const crop = Math.min(bitmap.width, bitmap.height);
  const out = Math.min(side, crop);
  const sx = (bitmap.width - crop) / 2;
  const sy = (bitmap.height - crop) / 2;
  const canvas = document.createElement('canvas');
  canvas.width = out;
  canvas.height = out;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas unsupported');
  ctx.drawImage(bitmap, sx, sy, crop, crop, 0, 0, out, out);
  if ('close' in bitmap) (bitmap as ImageBitmap).close();
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.72));
  if (!blob) throw new Error('encode failed');
  return { blob, width: out, height: out };
}

/** Shared family photos keep their shape; only the long side is capped. */
export async function preparePhoto(file: Blob, maxSide = 1600): Promise<PreparedImage> {
  const bitmap = await decode(file);
  const k = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
  const w = Math.max(1, Math.round(bitmap.width * k));
  const h = Math.max(1, Math.round(bitmap.height * k));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas unsupported');
  ctx.drawImage(bitmap, 0, 0, w, h);
  if ('close' in bitmap) (bitmap as ImageBitmap).close();
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.88));
  if (!blob) throw new Error('encode failed');
  return { blob, width: w, height: h };
}

async function decode(file: Blob): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file, { imageOrientation: 'from-image' } as ImageBitmapOptions);
    } catch {
      /* fall through to <img> */
    }
  }
  const url = URL.createObjectURL(file);
  try {
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('decode failed'));
      img.src = url;
    });
  } finally {
    // The bitmap is drawn synchronously by the caller right after this resolves,
    // so revoking on the next tick is safe.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}
